import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Dialog,
    DialogTitle,
    Button,
    Typography,
    Box,
    Alert,
    AlertTitle,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Radio,
    useTheme,
    useMediaQuery
} from '@mui/material';
import { styled, ThemeProvider, createTheme } from '@mui/material/styles';
import { X, Camera, LockOpen, CheckCircle, ShoppingCart } from 'lucide-react';
import { fetchLockedCapsules } from '../../api/GetCapsules.ts';
import { fetchAuthSession } from '@aws-amplify/auth';

interface Capsule {
    capsuleID: number;
    title: string;
    unlock: string;
    print?: boolean;
    imageCount?: number;
}

const theme = createTheme({
    palette: {
        primary: {
            main: '#7B2CBF',
        },
        background: {
            default: '#FFFFFF',
        },
    },
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    textTransform: 'none',
                    boxShadow: '0 4px 6px rgba(123, 44, 191, 0.1)',
                    '&:hover': {
                        boxShadow: '0 6px 8px rgba(123, 44, 191, 0.2)',
                    },
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: 16,
                    boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)',
                },
            },
        },
    },
});

const StyledDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialog-paper': {
        borderRadius: 16,
        overflow: 'hidden',
    },
}));

function PolaroidDeliveryDialog({ open, onClose, User }: { open: boolean; onClose: () => void, User: string; }) {
    const [selectedCapsule, setSelectedCapsule] = useState<string>('');
    const [capsules, setCapsules] = useState<Capsule[]>([]);
    const theme = useTheme();

    useEffect(() => {
        if (open) {
            fetchLockedCapsules(User)
                .then((data) => {
                    const now = new Date();
                    const filteredCapsules = data.filter((capsule) => {
                        const unlockDate = new Date(capsule.unlock);
                        const timeDiff = unlockDate.getTime() - now.getTime();
                        const daysDiff = timeDiff / (1000 * 3600 * 24);
                        return daysDiff > 14 && !capsule.print && capsule.images.length > 0;
                    });
                    setCapsules(filteredCapsules);
                })
        }
    }, [open, User]);

    const handleCheckout = async () => {
        const payload = {
            "capsule": selectedCapsule 
        }

        const apiUrl = `https://lfnawfdz0a.execute-api.ap-southeast-2.amazonaws.com/stripe/create-session/print`;
        
        try {
            const { tokens } = await fetchAuthSession();
            const authToken = tokens?.accessToken?.toString();

            fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'x-api-key': authToken!,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload)
            }).then((response) => response.json())
                .then((data) => {
                    window.location.href = data.url;
                })
                .catch((error) => {
                    console.error('Error:', error);
                })
        } catch (err) {
            console.error('Failed create stripe session: ', err);
            return null;
        }
        onClose();
    };

    return (
        <ThemeProvider theme={theme}>
            <StyledDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
                <Box sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'column', md: 'row' },
                    maxHeight: { xs: '100vh', md: 'none' },
                    overflow: { xs: 'auto', md: 'visible' }
                }}>
                    {/* Left side - Product Showcase */}
                    <Box sx={{ 
                        flex: { xs: 'none', md: 1 },
                        position: 'relative',
                        bgcolor: '#0A0F15',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: 4,
                        borderRadius: { xs: '16px 16px 0 0', md: '16px 0 0 16px' }
                    }}>
                        <IconButton 
                            onClick={onClose}
                            sx={{
                                position: 'absolute',
                                top: 16,
                                right: 16,
                                color: 'white',
                                '&:hover': { transform: 'scale(1.1)' },
                                transition: 'transform 0.2s'
                            }}
                        >
                            <X />
                        </IconButton>
                        
                        <Box sx={{ 
                            width: '100%',
                            maxWidth: { xs: 300, md: 400 },
                            aspectRatio: { xs: '4/3', md: '1' },
                            position: 'relative',
                            mb: 3
                        }}>
                            <img 
                                src="polaroid.jpg"
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    borderRadius: '16px',
                                    boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
                                }}
                                alt="Polaroid Preview"
                            />
                            <Box sx={{
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                right: 0,
                                background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
                                p: 2,
                                borderRadius: '0 0 16px 16px'
                            }}>
                                <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                                    Premium Polaroid Prints
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                                    High-quality prints delivered to your door
                                </Typography>
                            </Box>
                    </Box>

                        <Box sx={{ 
                            textAlign: 'center', 
                            color: 'white'
                        }}>
                            <Typography variant="h5" sx={{ mb: 2, background: 'linear-gradient(45deg, #7B2CBF, #E31B88)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                                Why Choose Our Prints?
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', justifyContent: 'center' }}>
                                <Box sx={{ textAlign: 'center', flex: '1 1 150px', maxWidth: 200 }}>
                                    <Camera size={32} style={{ marginBottom: 8, color: '#7B2CBF' }} />
                                    <Typography variant="subtitle2" sx={{ mb: 1, color: 'white' }}>
                                        Premium Quality
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                                        Professional-grade prints on premium paper
                                    </Typography>
                                </Box>
                                <Box sx={{ textAlign: 'center', flex: '1 1 150px', maxWidth: 200 }}>
                                    <CheckCircle size={32} style={{ marginBottom: 8, color: '#7B2CBF' }} />
                                    <Typography variant="subtitle2" sx={{ mb: 1, color: 'white' }}>
                                        Guaranteed Delivery
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                                        Secure packaging and tracked shipping
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Box>

                    {/* Right side - Selection & Checkout */}
                    <Box sx={{ 
                        flex: 1,
                        bgcolor: 'white',
                        p: 4,
                        display: 'flex',
                        flexDirection: 'column',
                        borderRadius: { xs: '0 0 16px 16px', md: '0 16px 16px 0' }
                    }}>
                        <Typography variant="h5" sx={{ mb: 3, color: '#0A0F15', fontWeight: 600 }}>
                            Print Your TimeCapsule
                        </Typography>

                        <Box sx={{ mb: 4 }}>
                            <Typography variant="subtitle1" sx={{ color: '#7B2CBF', mb: 1, fontWeight: 500 }}>
                                How It Works
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                                Select your TimeCapsule below and we'll print your memories as beautiful Polaroid-style photos. 
                                They'll be delivered right to your door on the unlock date.
                            </Typography>
                            
                            <Box sx={{ 
                                bgcolor: 'rgba(123, 44, 191, 0.05)',
                                p: 2,
                                borderRadius: 2,
                                border: '1px solid rgba(123, 44, 191, 0.1)',
                                mb: 3
                            }}>
                                <Typography variant="subtitle2" sx={{ color: '#7B2CBF', display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <ShoppingCart size={16} /> Pricing
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                                    $1.99 per image • Free shipping included
                                </Typography>
                            </Box>
                        </Box>

                        <Typography variant="subtitle1" sx={{ color: '#7B2CBF', mb: 2, fontWeight: 500 }}>
                            Select Your TimeCapsule
                        </Typography>

                        {capsules.length > 0 ? (
                            <>
                                <Box sx={{ flex: 1, overflow: 'auto' }}>
                                    <List sx={{ 
                                        '& > *:not(:last-child)': {
                                            borderBottom: '1px solid rgba(0,0,0,0.08)'
                                        }
                                    }}>
                                        {capsules.map((capsule) => (
                                <ListItem
                                    key={capsule.capsuleID}
                                    disablePadding
                                    sx={{
                                                    py: 2,
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s',
                                                    '&:hover': {
                                                        bgcolor: 'rgba(123, 44, 191, 0.05)'
                                                    }
                                                }}
                                            >
                                    <Radio
                                        checked={selectedCapsule === capsule.capsuleID.toString()}
                                        onChange={() => setSelectedCapsule(capsule.capsuleID.toString())}
                                        value={capsule.capsuleID}
                                        name="capsule-selection"
                                        sx={{
                                                        color: '#7B2CBF',
                                            '&.Mui-checked': {
                                                            color: '#7B2CBF',
                                            },
                                        }}
                                    />
                                                <ListItemText
                                                    primary={
                                                        <Typography variant="subtitle2" sx={{ color: '#0A0F15', fontWeight: 500 }}>
                                                            {capsule.title}
                                                        </Typography>
                                                    }
                                                    secondary={
                                                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                            Unlocks {new Date(capsule.unlock).toLocaleDateString()}
                                                            {capsule.imageCount && ` • ${capsule.imageCount} images`}
                                                        </Typography>
                                                    }
                                                    onClick={() => setSelectedCapsule(capsule.capsuleID.toString())}
                                    />
                                </ListItem>
                                        ))}
                                    </List>
                                </Box>

                                <Button
                                    variant="contained"
                                    onClick={handleCheckout}
                                    disabled={!selectedCapsule}
                                    sx={{
                                        mt: 3,
                                        py: 1.5,
                                        bgcolor: '#7B2CBF',
                                        '&:hover': {
                                            bgcolor: '#6A24A6'
                                        },
                                        '&.Mui-disabled': {
                                            bgcolor: 'rgba(123, 44, 191, 0.12)',
                                            color: 'rgba(123, 44, 191, 0.26)'
                                        }
                                    }}
                                >
                                    Proceed to Checkout
                                </Button>
                            </>
                        ) : (
                            <Alert 
                                severity="info" 
                                sx={{ 
                                    bgcolor: 'rgba(33, 150, 243, 0.05)',
                                    color: '#2196F3',
                                    '& .MuiAlert-icon': {
                                        color: '#2196F3'
                                    }
                                }}
                            >
                            <AlertTitle>No Capsules Available</AlertTitle>
                                You don't have any locked capsules with images available for printing at this time. 
                                Create a new capsule set to unlock in 14+ days to order this service.
                            </Alert>
                        )}
                    </Box>
                </Box>
            </StyledDialog>
        </ThemeProvider>
    );
}

function CapsuleUnlockDialog({
    open,
    onClose,
    unlockType,
    User,
}: {
    open: boolean;
    onClose: () => void;
    unlockType: 'single' | 'all';
    User: string;
}) {
    const [activeStep, setActiveStep] = useState(0);
    const [selectedCapsule, setSelectedCapsule] = useState<string>('');
    const [capsules, setCapsules] = useState<Capsule[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const steps = unlockType === 'single' ? ['Select Capsule', 'Confirm Unlock'] : ['Confirm Unlock'];

    useEffect(() => {
        if (open) {
            setLoading(true);
            fetchLockedCapsules(User)
                .then((data) => {
                    const lockedCapsules = data.filter(capsule => 
                        new Date(capsule.unlock) > new Date() && capsule.images.length > 0
                    );
                    setCapsules(lockedCapsules);
                })
                .catch((err) => setError(err.message))
                .finally(() => setLoading(false));
        }
        setActiveStep(0);
    }, [open, User, unlockType]);

    const handleNext = () => setActiveStep((prevStep) => prevStep + 1);
    const handleBack = () => setActiveStep((prevStep) => prevStep - 1);
    const handleComplete = async () => {
        let priceID;
        let payload;
        if (unlockType === "single") {
            //priceID = 'price_1QSwoBHWhHrfjpBauVGhtGRb'; test
            priceID = 'price_1RGCVWHWhHrfjpBa0USdtCiR';
            payload = {
                "priceId": priceID,
                "capsule": selectedCapsule 
            }
        } else {
            //priceID = 'price_1QUhU3HWhHrfjpBaBjpPgBYy'; test
            priceID = 'price_1RGCVUHWhHrfjpBaoxp4lUtd';
            payload = {
                "priceId": priceID
            }
        }

        const apiUrl = `https://lfnawfdz0a.execute-api.ap-southeast-2.amazonaws.com/stripe/create-session/unlock`;
        
        try {
            const { tokens } = await fetchAuthSession();
            const authToken = tokens?.accessToken?.toString();

            fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'x-api-key': authToken!,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload)
            }).then((response) => response.json())
                .then((data) => {
                    window.location.href = data.url;
                })
                .catch((error) => {
                    console.error('Error:', error);
                })
        } catch (err) {
            console.error('Failed create stripe session: ', err);
            return null;
        }
        onClose();
    };

    const price = unlockType === 'single' ? 5 : 12;

    const isUnlockDisabled = unlockType === 'single' 
        ? !selectedCapsule
        : capsules.length === 0;

    return (
        <ThemeProvider theme={theme}>
            <StyledDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
                <Box sx={{ 
                    bgcolor: 'white',
                    p: 4,
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: '16px'
                }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Typography variant="h5" sx={{ color: '#0A0F15', fontWeight: 600 }}>
                            {unlockType === 'single' ? 'Unlock One Capsule' : 'Unlock All Capsules'}
                        </Typography>
                        <IconButton 
                            onClick={onClose}
                            sx={{
                                color: '#0A0F15',
                                '&:hover': { transform: 'scale(1.1)' },
                                transition: 'transform 0.2s'
                            }}
                        >
                            <X />
                        </IconButton>
                    </Box>

                    <Box sx={{ mb: 4 }}>
                        <Typography variant="subtitle1" sx={{ color: '#7B2CBF', mb: 1, fontWeight: 500 }}>
                            How It Works
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                            {unlockType === 'single' 
                                ? 'Select your TimeCapsule below and unlock it instantly. Your memories will be immediately accessible.'
                                : 'Unlock all your TimeCapsules at once. Access all your memories immediately with a single purchase.'}
                        </Typography>
                        
                        {activeStep === 0 && (
                            <Box sx={{ 
                                bgcolor: 'rgba(123, 44, 191, 0.05)',
                                p: 2,
                                borderRadius: 2,
                                border: '1px solid rgba(123, 44, 191, 0.1)',
                                mb: 3
                            }}>
                                <Typography variant="subtitle2" sx={{ color: '#7B2CBF', display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <ShoppingCart size={16} /> Pricing
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                                    {unlockType === 'single' 
                                        ? `$${price} per capsule • Instant access`
                                        : `$${price} for all capsules • Best value`}
                                </Typography>
                            </Box>
                        )}

                        {((activeStep === 1 && unlockType === 'single') || (activeStep === 0 && unlockType === 'all')) && (
                            <Alert 
                                severity="warning" 
                                icon={<LockOpen />} 
                                sx={{ 
                                    mb: 3,
                                    bgcolor: 'rgba(255, 152, 0, 0.05)',
                                    border: '1px solid rgba(255, 152, 0, 0.1)',
                                    '& .MuiAlert-icon': {
                                        color: '#FF9800'
                                    }
                                }}
                            >
                                <AlertTitle>Unlock Warning</AlertTitle>
                                {unlockType === 'single'
                                    ? 'This will immediately unlock the selected capsule, allowing you to access its contents.'
                                    : 'This will immediately unlock all of your capsules, allowing you to access their contents.'}
                        </Alert>
                        )}
                    </Box>

                    <AnimatePresence mode="wait">
                        <motion.div
                            key={activeStep}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.3 }}
                        >
                            {activeStep === 0 && unlockType === 'single' && (
                                <>
                                    <Typography variant="subtitle1" sx={{ color: '#7B2CBF', mb: 2, fontWeight: 500 }}>
                                        Select Your TimeCapsule
                                        </Typography>
                                    <Box sx={{ maxHeight: '300px', overflow: 'auto', mb: 3 }}>
                                        <List sx={{ 
                                            '& > *:not(:last-child)': {
                                                borderBottom: '1px solid rgba(0,0,0,0.08)'
                                            }
                                        }}>
                                            {capsules.map((capsule) => (
                                                <ListItem
                                                    key={capsule.capsuleID}
                                                    disablePadding
                                                    sx={{
                                                        py: 2,
                                                        cursor: 'pointer',
                                                        transition: 'all 0.2s',
                                                        '&:hover': {
                                                            bgcolor: 'rgba(123, 44, 191, 0.05)'
                                                        }
                                                    }}
                                                >
                                                    <Radio
                                                        checked={selectedCapsule === capsule.capsuleID.toString()}
                                                        onChange={() => setSelectedCapsule(capsule.capsuleID.toString())}
                                                        value={capsule.capsuleID}
                                                        name="capsule-selection"
                                                        sx={{
                                                            color: '#7B2CBF',
                                                            '&.Mui-checked': {
                                                                color: '#7B2CBF',
                                                            },
                                                        }}
                                                    />
                                                    <ListItemText
                                                        primary={
                                                            <Typography variant="subtitle2" sx={{ color: '#0A0F15', fontWeight: 500 }}>
                                                                {capsule.title}
                                                            </Typography>
                                                        }
                                                        secondary={
                                                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                                Unlocks {new Date(capsule.unlock).toLocaleDateString()}
                                                                {capsule.imageCount && ` • ${capsule.imageCount} images`}
                                                            </Typography>
                                                        }
                                                        onClick={() => setSelectedCapsule(capsule.capsuleID.toString())}
                                                    />
                                                </ListItem>
                                            ))}
                                        </List>
                                    </Box>
                                </>
                            )}

                            {((activeStep === 1 && unlockType === 'single') || (activeStep === 0 && unlockType === 'all')) && (
                                <Box sx={{ mt: 2 }}>
                                    {unlockType === 'single' ? (
                                        <Box sx={{ mb: 3 }}>
                                            <Typography variant="subtitle2" sx={{ color: '#0A0F15', mb: 1 }}>
                                                Selected Capsule
                                        </Typography>
                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                {capsules.find((c) => c.capsuleID === parseInt(selectedCapsule))?.title}
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <Box sx={{ mb: 3 }}>
                                            <Typography variant="subtitle2" sx={{ color: '#0A0F15', mb: 1 }}>
                                                Total Capsules
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                {capsules.length} capsules will be unlocked
                                            </Typography>
                                        </Box>
                                    )}

                                    {unlockType === 'single' && (
                                        <Box sx={{ 
                                            bgcolor: 'rgba(123, 44, 191, 0.05)',
                                            p: 2,
                                            borderRadius: 2,
                                            border: '1px solid rgba(123, 44, 191, 0.1)',
                                            mb: 3
                                        }}>
                                            <Typography variant="subtitle2" sx={{ color: '#7B2CBF', display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <ShoppingCart size={16} /> Total Price
                                                </Typography>
                                            <Typography variant="h6" sx={{ mt: 1, color: '#7B2CBF' }}>
                                                ${price}
                                                </Typography>
                                        </Box>
                                        )}
                                </Box>
                            )}
                        </motion.div>
                    </AnimatePresence>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                        {activeStep > 0 && unlockType === 'single' && (
                            <Button
                                onClick={handleBack}
                                variant="outlined"
                                sx={{
                                    color: '#7B2CBF',
                                    borderColor: '#7B2CBF',
                                    '&:hover': {
                                        borderColor: '#6A24A6',
                                        bgcolor: 'rgba(123, 44, 191, 0.05)'
                                    }
                                }}
                            >
                                Back
                            </Button>
                        )}
                        {(activeStep < steps.length - 1 && unlockType === 'single') ? (
                            <Button
                                onClick={handleNext}
                                variant="contained"
                                disabled={!selectedCapsule}
                                sx={{
                                    ml: 'auto',
                                    bgcolor: '#7B2CBF',
                                    '&:hover': {
                                        bgcolor: '#6A24A6'
                                    },
                                    '&.Mui-disabled': {
                                        bgcolor: 'rgba(123, 44, 191, 0.12)',
                                        color: 'rgba(123, 44, 191, 0.26)'
                                    }
                                }}
                            >
                                Next
                            </Button>
                        ) : (
                            <Button
                                onClick={handleComplete}
                                variant="contained"
                                disabled={isUnlockDisabled}
                                sx={{
                                    ml: 'auto',
                                    bgcolor: '#7B2CBF',
                                    '&:hover': {
                                        bgcolor: '#6A24A6'
                                    },
                                    '&.Mui-disabled': {
                                        bgcolor: 'rgba(123, 44, 191, 0.12)',
                                        color: 'rgba(123, 44, 191, 0.26)'
                                    }
                                }}
                            >
                                Unlock Now
                            </Button>
                        )}
                    </Box>
                </Box>
            </StyledDialog>
        </ThemeProvider>
    );
}

export { PolaroidDeliveryDialog, CapsuleUnlockDialog };